import { useState } from 'react';
import { Autocomplete } from '@react-google-maps/api';
import TextField from './TextField';
import { useTranslation } from 'react-i18next';

interface ParsedAddress {
  street: string;
  aptSuite: string;
  city: string;
  state: string;
  zipCode: string;
}

interface AddressAutocompleteProps {
  label?: string;
  onAddressSelect: (address: ParsedAddress) => void;
}

export default function AddressAutocomplete({ label, onAddressSelect }: AddressAutocompleteProps) {
  const [autocomplete, setAutocomplete] = useState<google.maps.places.Autocomplete | null>(null);
  const [address, setAddress] = useState('');
  const { t } = useTranslation();

  const handleLoad = (instance: google.maps.places.Autocomplete) => {
    setAutocomplete(instance);
  };

  const handlePlaceChanged = () => {
    if (!autocomplete) return;

    const place = autocomplete.getPlace();
    const components = place.address_components || [];

    const get = (type: string, nameType: 'long_name' | 'short_name' = 'long_name') => {
      const match = components.find((c) => c.types.includes(type));
      return match ? match[nameType] : '';
    };

    const streetNumber = get('street_number');
    const route = get('route');
    const street = [streetNumber, route].filter(Boolean).join(' ');
    const aptSuite = get('subpremise');
    const city = get('locality') || get('sublocality') || get('administrative_area_level_2');
    const state = get('administrative_area_level_1', 'short_name');
    const zipCode = get('postal_code');

    onAddressSelect({ street, aptSuite, city, state, zipCode });
    setAddress(place.formatted_address || '');
  };

  return (
    <Autocomplete onLoad={handleLoad} onPlaceChanged={handlePlaceChanged}>
      <TextField
        label={label || t('SearchAddress', 'Search Address')}
        variant="outlined"
        fullWidth
        value={address}
        onChange={(e) => setAddress(e.target.value)}
      />
    </Autocomplete>
  );
}
